import { Badge, Button, Card, CardContent, CardHeader, CardTitle, Input, ScrollArea, Skeleton } from '@ibsys/kit';
import { FC, ReactNode } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { KnowledgeNodeType } from '@/entities/knowledge';
import { PredicateInput } from '@/features/PredicateInput';
import { AiTextarea, ConfirmationButton, EditableTitle } from '@/shared/ui';
import { KnowledgeFormValues } from './types';

type Props = {
  nodeId?: number;
  isCreate: boolean;
  isLoadingData: boolean;
  isLoading?: boolean;
  isDeleting?: boolean;
  isDeleted?: boolean;
  nodeType?: KnowledgeNodeType;
  relationsSlot?: ReactNode;
  onSave: () => void;
  onDelete?: () => void;
  onTitleSave: (value: string) => void;
};

type HeaderTitleProps = {
  isCreate: boolean;
  nodeType?: KnowledgeNodeType;
  nodeTypeLabel: string;
  nameValue: string;
  disabled?: boolean;
  isLoading?: boolean;
  onTitleSave: (value: string) => void;
  createTitle: string;
};

const HeaderTitle: FC<HeaderTitleProps> = ({
  isCreate,
  nodeType,
  nodeTypeLabel,
  nameValue,
  disabled,
  isLoading,
  onTitleSave,
  createTitle,
}) => {
  const { t } = useTranslation('knowledge');

  if (isCreate) {
    return <CardTitle>{createTitle}</CardTitle>;
  }

  return (
    <div className="flex items-center gap-4">
      {nodeType && (
        <Badge variant="secondary" outline>
          {nodeTypeLabel}
        </Badge>
      )}
      <EditableTitle value={nameValue} label={t('renameNode')} disabled={disabled || isLoading} onSave={onTitleSave} />
    </div>
  );
};

export const KnowledgeForm: FC<Props> = (props) => {
  const {
    nodeId,
    isCreate,
    isLoadingData,
    isLoading,
    isDeleting,
    isDeleted,
    nodeType,
    relationsSlot,
    onSave,
    onDelete,
    onTitleSave,
  } = props;

  const { t } = useTranslation(['knowledge', 'common']);
  const { control, watch, formState, setValue } = useFormContext<KnowledgeFormValues>();
  const { disabled } = formState;

  const nameValue = watch('name');
  const textValue = watch('text');

  const nodeTypeLabel = nodeType ? t(`knowledge:nodeTypes.${nodeType}`) : '';

  return (
    <Card className="h-full flex flex-col">
      <CardHeader className="flex flex-wrap items-center gap-3 border-b">
        {isLoadingData ? (
          <Skeleton className="h-8 w-64" />
        ) : (
          <HeaderTitle
            isCreate={isCreate}
            nodeType={nodeType}
            nodeTypeLabel={nodeTypeLabel}
            nameValue={nameValue ?? ''}
            disabled={disabled}
            isLoading={isLoading}
            onTitleSave={onTitleSave}
            createTitle={t('knowledge:newKnowledge')}
          />
        )}

        <div className="flex gap-4 ml-auto items-center">
          {nodeId && !isCreate && onDelete && !disabled && !isDeleted && (
            <ConfirmationButton
              label={t('knowledge:deleteNode')}
              onConfirm={onDelete}
              isLoadingConfirm={isDeleting}
              title={t('knowledge:deleteConfirm')}
              trigger={{ size: 'sm' }}
            />
          )}
          {!disabled && (
            <Button variant="default" onClick={onSave} size="sm" isLoading={isLoading}>
              {t('common:save')}
            </Button>
          )}
        </div>
      </CardHeader>

      <CardContent className="flex-1 overflow-hidden p-0">
        <ScrollArea className="h-full">
          <div className="@container/knowledge-form flex flex-col gap-6 p-6">
            {isLoadingData ? (
              <Skeleton className="w-full h-40" />
            ) : (
              <div className="grid grid-cols-1 @md/knowledge-form:grid-cols-2 gap-4">
                {isCreate && <Input control={control} name="name" label={t('knowledge:name')} className="col-span-2" />}
                <AiTextarea
                  value={textValue || ''}
                  onChange={(event) => setValue('text', event.target.value, { shouldDirty: true })}
                  name="text"
                  disabled={disabled}
                  label={t('knowledge:text')}
                  style={{ resize: 'none' }}
                  classNames={{ input: 'min-h-full' }}
                />
                <PredicateInput
                  control={control}
                  name="predicate"
                  disabled={disabled}
                  label={t('knowledge:predicate')}
                  classNames={{ input: 'min-h-full!' }}
                />
              </div>
            )}
            {relationsSlot}
          </div>
        </ScrollArea>
      </CardContent>
    </Card>
  );
};
